import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { DropdownContainer } from "../bits/inputs/DropdownContainer";
import { PrimaryButton, SecondaryButton } from "../bits/buttons";
import { css, useTheme } from "@emotion/react";
import { Box } from "@mui/material";
import Papa from "papaparse";
import { trpc } from "../../utils/trpc";

type CsvRow = {
  Entries: string;
};

type UploadFrjTagFilterFormProps = {
  onSubmit: (msg: string) => void;
};

export const UploadFrjTagFilterForm = ({
  onSubmit,
}: UploadFrjTagFilterFormProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>("");
  const theme = useTheme();

  const uploadFrjTagFilter = trpc.uploadFrjTagFilter.useMutation({
    onSuccess: () => {
      onSubmit("FRJ tag filter uploaded successfully.");
      setFiles([]);
    },
    onError: () =>
      setError(
        "Error uploading tag filter — please check the file format and try again.",
      ),
  });

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (incoming) => setFiles(incoming),
    accept: { "text/csv": [".csv"] },
    maxFiles: 1,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    onSubmit("");
    if (!files.length) {
      setError("Please select a file");
      return;
    }
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (!reader.result || typeof reader.result !== "string") {
        setError("Error reading file");
        return;
      }
      const parsed = Papa.parse<CsvRow>(reader.result.trim(), {
        header: true,
        skipEmptyLines: true,
        delimiter: "\n",
      });
      if (parsed.errors.length) {
        console.error(parsed);
        setError("Error parsing file");
        return;
      }
      if (!parsed.meta.fields?.includes("Entries")) {
        setError("Error parsing file — missing required Entries header.");
        return;
      }
      const tags = parsed.data
        .map((row) => row.Entries?.trim())
        .filter(Boolean) as string[];
      uploadFrjTagFilter.mutate({ tags });
    });
    reader.addEventListener("error", () => setError("Error reading file"));
    reader.readAsText(files[0]);
  };

  const downloadExampleFile = () => {
    const content =
      "Entries\nAdd-backs\nAdjournment of Capital Claims\nAppeals\nArbitration\nBankruptcy";
    const blob = new Blob([content], { type: "text/csv" });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = "exampleFrjTagFilter.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2, paddingTop: 2 }}
    >
      {!files.length && (
        <DropdownContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <p>Drag and drop or click here to upload the FRJ tag filter CSV</p>
        </DropdownContainer>
      )}
      {!!files.length && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <p>Selected file:</p>
          {files.map((file) => (
            <div
              key={file.name}
              css={css`
                display: flex;
                justify-content: space-between;
                width: 100%;
              `}
            >
              <span>{file.name}</span>
              <span>{Math.round(file.size / 1024)} KB</span>
              <PrimaryButton type="button" onClick={() => setFiles([])}>
                Delete
              </PrimaryButton>
            </div>
          ))}
        </div>
      )}
      <SecondaryButton type="button" onClick={downloadExampleFile}>
        Download example file
      </SecondaryButton>
      <PrimaryButton type="submit">Upload FRJ Tag Filter</PrimaryButton>
      <p style={{ color: theme.palette.error.main }}>{error}</p>
    </Box>
  );
};
